// ── PlayerProgress System ──
// Pure functions for managing player progression, stars, and stats.
// All state is immutable — functions return new objects.

import { saveGameProgress, loadGameProgress } from '@/sdk/funway';

const STORAGE_KEY = 'resonance_progress';

// ── Types ──

export interface PuzzleSolveRecord {
  solved: boolean;
  bestStars: number;
  bestScore: number;
}

export interface DailySolveRecord {
  score: number;
  strikes: number;
  stars: number;
}

export interface PlayerProgress {
  journey: {
    chaptersUnlocked: number;
    puzzlesSolved: Record<string, PuzzleSolveRecord>;
    stars: Record<string, number>;
  };
  daily: {
    currentStreak: number;
    longestStreak: number;
    lastSolveDate: string | null;
    history: Record<string, DailySolveRecord>;
  };
  stats: {
    totalPuzzlesSolved: number;
    totalStrikesMade: number;
    perfectSolves: number;
  };
}

// ── Default Progress ──

export function getDefaultProgress(): PlayerProgress {
  return {
    journey: {
      chaptersUnlocked: 1,
      puzzlesSolved: {},
      stars: {},
    },
    daily: {
      currentStreak: 0,
      longestStreak: 0,
      lastSolveDate: null,
      history: {},
    },
    stats: {
      totalPuzzlesSolved: 0,
      totalStrikesMade: 0,
      perfectSolves: 0,
    },
  };
}

// ── Persistence ──

export function loadProgress(): PlayerProgress {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return getDefaultProgress();

    const parsed: unknown = JSON.parse(raw);
    if (!isValidProgress(parsed)) return getDefaultProgress();

    return parsed;
  } catch {
    return getDefaultProgress();
  }
}

export function saveProgress(progress: PlayerProgress): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch {
    /* localStorage full or unavailable — ignore */
  }

  // Also save to Funway SDK (async, fire-and-forget)
  saveGameProgress(progress as unknown as Record<string, unknown>).catch(
    () => {},
  );
}

/**
 * Async variant: tries SDK cloud storage first, then falls back to localStorage.
 * Call after SDK init to potentially restore cloud-saved progress.
 */
export async function loadProgressAsync(): Promise<PlayerProgress> {
  const sdkData = await loadGameProgress();
  if (sdkData && isValidProgress(sdkData)) {
    return sdkData as unknown as PlayerProgress;
  }
  return loadProgress();
}

// ── Validation ──

function isValidProgress(value: unknown): value is PlayerProgress {
  if (typeof value !== 'object' || value === null) return false;

  const obj = value as Record<string, unknown>;

  // Check journey
  if (typeof obj['journey'] !== 'object' || obj['journey'] === null) return false;
  const journey = obj['journey'] as Record<string, unknown>;
  if (typeof journey['chaptersUnlocked'] !== 'number') return false;
  if (typeof journey['puzzlesSolved'] !== 'object' || journey['puzzlesSolved'] === null) return false;
  if (typeof journey['stars'] !== 'object' || journey['stars'] === null) return false;

  // Check daily
  if (typeof obj['daily'] !== 'object' || obj['daily'] === null) return false;
  const daily = obj['daily'] as Record<string, unknown>;
  if (typeof daily['currentStreak'] !== 'number') return false;
  if (typeof daily['longestStreak'] !== 'number') return false;
  if (daily['lastSolveDate'] !== null && typeof daily['lastSolveDate'] !== 'string') return false;
  if (typeof daily['history'] !== 'object' || daily['history'] === null) return false;

  // Check stats
  if (typeof obj['stats'] !== 'object' || obj['stats'] === null) return false;
  const stats = obj['stats'] as Record<string, unknown>;
  if (typeof stats['totalPuzzlesSolved'] !== 'number') return false;
  if (typeof stats['totalStrikesMade'] !== 'number') return false;
  if (typeof stats['perfectSolves'] !== 'number') return false;

  return true;
}

// ── Recording ──

export function recordPuzzleSolve(
  progress: PlayerProgress,
  puzzleId: string,
  score: number,
  stars: number,
  strikes: number,
): PlayerProgress {
  const existing = progress.journey.puzzlesSolved[puzzleId];
  const wasAlreadySolved = existing?.solved ?? false;

  const newRecord: PuzzleSolveRecord = {
    solved: true,
    bestStars: Math.max(existing?.bestStars ?? 0, stars),
    bestScore: Math.max(existing?.bestScore ?? 0, score),
  };

  const newStars = Math.max(progress.journey.stars[puzzleId] ?? 0, stars);

  // Count solved puzzles per chapter to determine chapter unlocks
  const newPuzzlesSolved = {
    ...progress.journey.puzzlesSolved,
    [puzzleId]: newRecord,
  };

  const newStarsMap = {
    ...progress.journey.stars,
    [puzzleId]: newStars,
  };

  // Calculate chapters unlocked
  const chaptersUnlocked = calculateChaptersUnlocked(newPuzzlesSolved);

  return {
    journey: {
      chaptersUnlocked: Math.max(progress.journey.chaptersUnlocked, chaptersUnlocked),
      puzzlesSolved: newPuzzlesSolved,
      stars: newStarsMap,
    },
    daily: { ...progress.daily },
    stats: {
      totalPuzzlesSolved: progress.stats.totalPuzzlesSolved + (wasAlreadySolved ? 0 : 1),
      totalStrikesMade: progress.stats.totalStrikesMade + strikes,
      perfectSolves: progress.stats.perfectSolves + (stars === 3 && !wasAlreadySolved ? 1 : 0),
    },
  };
}

export function recordDailySolve(
  progress: PlayerProgress,
  date: string,
  score: number,
  strikes: number,
  stars: number,
): PlayerProgress {
  const existing = progress.daily.history[date];

  // If already solved today, only update if better score
  if (existing && existing.score >= score) {
    return progress;
  }

  const newHistory = {
    ...progress.daily.history,
    [date]: { score, strikes, stars },
  };

  return {
    journey: { ...progress.journey },
    daily: {
      ...progress.daily,
      history: newHistory,
    },
    stats: {
      totalPuzzlesSolved: progress.stats.totalPuzzlesSolved + (existing ? 0 : 1),
      totalStrikesMade: progress.stats.totalStrikesMade + strikes,
      perfectSolves: progress.stats.perfectSolves + (stars === 3 && !existing ? 1 : 0),
    },
  };
}

// ── Unlocking ──

export function isPuzzleUnlocked(
  progress: PlayerProgress,
  _puzzleId: string,
  chapter: number,
  index: number,
): boolean {
  // Chapter must be unlocked
  if (!isChapterUnlocked(progress, chapter)) return false;

  // First puzzle of each chapter is always unlocked
  if (index <= 1) return true;

  // Previous puzzle in the chapter must be solved
  const prevIndex = index - 1;
  const prevId = `ch${chapter}-${String(prevIndex).padStart(2, '0')}`;
  const prevRecord = progress.journey.puzzlesSolved[prevId];

  return prevRecord?.solved ?? false;
}

export function isChapterUnlocked(progress: PlayerProgress, chapter: number): boolean {
  // Chapter 1 is always unlocked
  if (chapter <= 1) return true;

  // Chapter N+1 unlocked when chapter N has at least 7/10 puzzles solved
  return progress.journey.chaptersUnlocked >= chapter;
}

// ── Internal Helpers ──

function calculateChaptersUnlocked(puzzlesSolved: Record<string, PuzzleSolveRecord>): number {
  let unlocked = 1;

  for (let ch = 1; ch <= 6; ch++) {
    let solvedInChapter = 0;
    for (let i = 1; i <= 10; i++) {
      const id = `ch${ch}-${String(i).padStart(2, '0')}`;
      if (puzzlesSolved[id]?.solved) {
        solvedInChapter++;
      }
    }
    if (solvedInChapter >= 7) {
      unlocked = Math.max(unlocked, ch + 1);
    } else {
      break;
    }
  }

  return unlocked;
}
